import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { canonical, collectionPageSchema } from "@/utils/seo";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import ArticleCard from "@/components/base/ArticleCard";
import StoreCard from "@/components/base/StoreCard";
import { getTagBySlug, getPostsByTag, getStoresByTag } from "@/api/content";
import type { TagInfo, PostCard, StoreCard as StoreCardType } from "@/types/content";

export default function TagDetailPage({ initialData }: { initialData?: TagInfo | null }) {
  const { slug } = useParams<{ slug: string }>();
  const [tag, setTag] = useState<TagInfo | null>(initialData ?? null);
  const [posts, setPosts] = useState<PostCard[]>([]);
  const [storeCards, setStoreCards] = useState<StoreCardType[]>([]);
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (!slug) return;
    const decoded = decodeURIComponent(slug);
    getTagBySlug(slug).then((t) => {
      if (t) {
        setTag(t);
        Promise.all([
          getPostsByTag(decoded),
          getStoresByTag(decoded),
        ]).then(([p, s]) => {
          setPosts(p);
          setStoreCards(s);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [slug]);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  if (!tag) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">找不到此標籤</h2>
        <a
          href="/tags"
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          瀏覽所有標籤
        </a>
      </div>
    );
  }

  const totalCount = posts.length + storeCards.length;

  return (
    <>
      <SEO
        meta={{
          title: `${tag.name}｜美好時光指南標籤`,
          description: `瀏覽「${tag.name}」相關的文章與探店店家，共 ${totalCount} 個結果。`,
          canonical: canonical(`/tags/${tag.slug}`),
          keywords: [tag.name, "標籤", "探店", "美好時光指南"],
          structuredData: collectionPageSchema(`${tag.name}｜美好時光指南標籤`, `瀏覽「${tag.name}」相關的文章與探店店家。`, `/tags/${tag.slug}`),
        }}
      />

      {/* Tag Header */}
      <div className="w-full bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
              標籤
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            #{tag.name}
          </h1>
          <p className="text-slate-500">
            共 {totalCount} 個結果
            {posts.length > 0 && ` · 文章 ${posts.length} 篇`}
            {storeCards.length > 0 && ` · 探店店家 ${storeCards.length} 家`}
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          {/* Posts */}
          {posts.length > 0 && (
            <div className="mb-12">
              <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-6 md:mb-8 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-article-line text-primary" />
                </div>
                相關文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <ArticleCard key={post.id} post={post} />
                ))}
              </div>
            </div>
          )}

          {/* Stores */}
          {storeCards.length > 0 && (
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-6 md:mb-8 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-store-2-line text-primary" />
                </div>
                探店店家
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {storeCards.map((store) => (
                  <StoreCard key={store.id} store={store} />
                ))}
              </div>
            </div>
          )}

          {totalCount === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-slate-100">
                <i className="ri-search-line text-2xl text-slate-400" />
              </div>
              <p className="text-slate-500 mb-1">暫無「{tag.name}」相關內容</p>
              <a href="/tags" className="text-primary text-sm hover:underline">
                瀏覽其他標籤
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
}